import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import PreviewCode from './PreviewCode';
import { getAuthErrorMessage } from '../utils/errorMessages';
import { useInvitationAcceptance } from '../hooks/useInvitationAcceptance';
import { InvitationBanner, InvitationError } from './shared/InvitationNotice';
import { Button, IconButton, Input, Heading, Text } from './ui';
import { safeReturnTo, withReturnTo } from '../utils/returnTo';
import '../styles/Login.css';

const UserIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const LockIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const GoogleIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const AppleIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="#2A2A24">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.48-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.05-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

function PasswordToggleIcon({ visible }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {visible ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      )}
    </svg>
  );
}

function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  // An invitee who already has an account cannot sign up again — the route
  // out is logging in, carrying the token with them.
  const [emailAlreadyInUse, setEmailAlreadyInUse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPreviewCode, setShowPreviewCode] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const navigate = useNavigate();
  // A page this sign-up came from (a team's join page): it decides where to
  // land by itself, as an invitation does — no preview code, no waitlist.
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const {
    invitationToken,
    invitationError,
    accepting: acceptingInvitation,
    acceptInvitation,
  } = useInvitationAcceptance();

  // Re-run acceptance for an account that already exists, after a failure
  // the invitee may be able to clear (a flaky network, a resent invitation).
  const handleRetryInvitation = async () => {
    setLoading(true);
    const retry = await acceptInvitation();
    setLoading(false);
    if (retry.ok) navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password should be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Create auth user but don't create database document yet
      const userCredential = await authService.signUp(
        email,
        password,
        displayName,
        null
      );
      setNewUser(userCredential.user);

      // If there's an invitation, accept it and grant preview access
      if (invitationToken) {
        // Create the user doc without access; acceptProjectInvitation grants it
        // server-side once it has validated the token.
        await userService.createUser(userCredential.user.uid, {
          email: userCredential.user.email,
          displayName: displayName || userCredential.user.displayName || '',
          photoURL: userCredential.user.photoURL || '',
          hasPreviewAccess: false,
          joinedWaitlistAt: new Date(),
        });

        const invitationResult = await acceptInvitation();
        if (invitationResult.ok) {
          // Skip preview code and go straight to the app
          navigate('/');
          return;
        }
        // The account exists; only the invitation failed. Say why and offer
        // a retry — never fall through to the preview-code screen, which
        // asks for a code an invitee was never issued.
        setLoading(false);
        return;
      }

      // `signUp` wrote the account's doc; the page it came from takes over
      if (returnTo) {
        navigate(returnTo);
        return;
      }

      // Only show preview code if there's no invitation
      setShowPreviewCode(true);
      setLoading(false);
    } catch (error) {
      setError(getAuthErrorMessage(error));
      setEmailAlreadyInUse(error?.code === 'auth/email-already-in-use');
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await authService.signInWithGoogle();

      if (returnTo && !invitationToken) {
        if (result.isNewUser) await authService.createWaitlistUser(result.user);
        navigate(returnTo);
        return;
      }

      if (result.isNewUser) {
        setNewUser(result.user);

        // If there's an invitation, accept it and grant preview access
        if (invitationToken) {
          // Create the user doc without access; acceptProjectInvitation grants it
          // server-side once it has validated the token.
          await userService.createUser(result.user.uid, {
            email: result.user.email,
            displayName: result.user.displayName || '',
            photoURL: result.user.photoURL || '',
            hasPreviewAccess: false,
            joinedWaitlistAt: new Date(),
          });

          const invitationResult = await acceptInvitation();
          if (invitationResult.ok) {
            navigate('/');
            return;
          }
          // The account exists; only the invitation failed (see above).
          return;
        }

        // Only show preview code if there's no invitation
        setShowPreviewCode(true);
      } else if (result.hasPreviewAccess) {
        navigate('/');
      } else {
        navigate('/waitlist');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await authService.signInWithApple();

      if (returnTo && !invitationToken) {
        if (result.isNewUser) await authService.createWaitlistUser(result.user);
        navigate(returnTo);
        return;
      }

      if (result.isNewUser) {
        setNewUser(result.user);

        // If there's an invitation, accept it and grant preview access
        if (invitationToken) {
          // Create the user doc without access; acceptProjectInvitation grants it
          // server-side once it has validated the token.
          await userService.createUser(result.user.uid, {
            email: result.user.email,
            displayName: result.user.displayName || '',
            photoURL: result.user.photoURL || '',
            hasPreviewAccess: false,
            joinedWaitlistAt: new Date(),
          });

          const invitationResult = await acceptInvitation();
          if (invitationResult.ok) {
            navigate('/');
            return;
          }
          // The account exists; only the invitation failed (see above).
          return;
        }

        // Only show preview code if there's no invitation
        setShowPreviewCode(true);
      } else if (result.hasPreviewAccess) {
        navigate('/');
      } else {
        navigate('/waitlist');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewCodeComplete = (hasAccess) => {
    if (hasAccess) {
      navigate('/');
    } else {
      navigate('/waitlist');
    }
  };

  if (showPreviewCode && newUser) {
    return (
      <PreviewCode user={newUser} onComplete={handlePreviewCodeComplete} />
    );
  }

  return (
    <div className="login-container">
      <Link
        to={invitationToken ? `/login?invitation=${invitationToken}` : '/login'}
        className="back-to-welcome"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </Link>
      <div className="login-box">
        <div className="logo-container">
          <img src="/FullLogo.png" alt="Housfully" className="logo" />
        </div>
        <Heading level={1} tone="dark" className="preview-title">
          Preview Edition
        </Heading>
        <Text tone="dark" className="subtitle">
          {invitationToken
            ? 'Create your account to accept the project invitation'
            : 'Create your account to get on the waitlist. Or, get in touch with us to get a preview code and get access now: '}
          {!invitationToken && (
            <a href="mailto:hello@housfully.com" className="subtitle-link">
              hello@housfully.com
            </a>
          )}
        </Text>
        {invitationToken && (
          <InvitationBanner>
            You've been invited to view a project. Sign up to accept the
            invitation.
          </InvitationBanner>
        )}
        <InvitationError
          message={invitationError}
          onRetry={handleRetryInvitation}
          disabled={loading || acceptingInvitation}
        />
        {error && <div className="error-message">{error}</div>}
        {emailAlreadyInUse && returnTo && !invitationToken && (
          <InvitationBanner>
            You already have an account.{' '}
            <Link to={withReturnTo('/signin', returnTo)}>Log in instead</Link>.
          </InvitationBanner>
        )}
        {emailAlreadyInUse && invitationToken && (
          <InvitationBanner>
            You already have an account.{' '}
            <Link to={`/signin?invitation=${invitationToken}`}>
              Log in to accept the invitation
            </Link>
            .
          </InvitationBanner>
        )}
        <form onSubmit={handleSubmit} className="login-form">
          <Input
            tone="dark"
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display Name"
            disabled={loading}
            leadingIcon={UserIcon}
          />
          <Input
            tone="dark"
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            disabled={loading}
            leadingIcon={MailIcon}
          />
          <Input
            tone="dark"
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            disabled={loading}
            leadingIcon={LockIcon}
            trailingInteractive
            trailingIcon={
              <IconButton
                aria-label={
                  showPassword ? 'Hide password' : 'Toggle password visibility'
                }
                tone="dark"
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <PasswordToggleIcon visible={showPassword} />
              </IconButton>
            }
          />
          <Button
            type="submit"
            tone="dark"
            variant="primary"
            fullWidth
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>
        <div className="login-oauth">
          <Button
            type="button"
            tone="dark"
            variant="soft"
            fullWidth
            disabled={loading}
            onClick={handleGoogleSignIn}
            leadingIcon={GoogleIcon}
          >
            Continue with Google
          </Button>
          <Button
            type="button"
            tone="dark"
            variant="soft"
            fullWidth
            disabled={loading}
            onClick={handleAppleSignIn}
            leadingIcon={AppleIcon}
          >
            Continue with Apple
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
